import React, { useCallback, useEffect, useState } from 'react';
import type { Order, Vehicle } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useOrderUpdateApi } from '@hero/api-hooks';
import type { OrderUpdateAPI } from '@hero/api';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button';
import { VehiclePickerList } from '../../Vehicle/Card/VehiclePickerList';
import { Badge } from '../../../ui/Badge';
import Spinner from '../../../app/Spinner/Spinner';
import { CircleCheckIcon, ArrowRightIcon } from '@hero/web-icons';
import { OrderRouteDetails } from '../Drawer/OrderDetailsTab/OrderRouteDetails';
import { ShipmentDetails } from '../Drawer/OrderDetailsTab/ShipmentDetails';
import { LoadDetails } from '../Drawer/OrderDetailsTab/LoadDetails';
import { OrderPriceDetails } from '../Drawer/OrderDetailsTab/OrderPriceDetails';
import { useOrderDisplayInfo } from '@hero/hooks-shared';
import { useViewerLeg } from '@hero/web-hooks';

type BookStep = 'review' | 'select_vehicle' | 'booked';

export interface BookOrderDrawerProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onBooked?: () => void;
}

export function BookOrderDrawer({ order, isOpen, onClose, onBooked }: BookOrderDrawerProps) {
  const { t } = useTranslation(['m-order', 'c-main']);
  const [step, setStep] = useState<BookStep>('review');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | undefined>(undefined);

  const { mutate: updateOrder, isPending: isBooking } = useOrderUpdateApi({
    onSuccess: () => {
      toast.success(t('toast.success.order_updated'));
      setStep('booked');
      onBooked?.();
    },
    onError: (error: Error) => {
      toast.error(t('toast.error.order_update', { message: error.message }));
    },
  });

  const handleBook = useCallback(() => {
    if (!order || !selectedVehicle) return;
    const payload: OrderUpdateAPI['req'] = {
      _id: order._id,
      action: 'book_order',
      vehicle: { operation: 'add', _id: getObjectId(String(selectedVehicle._id)) },
    };
    if (selectedVehicle.driver?.driverId) {
      payload.driver = {
        operation: 'add',
        _id: getObjectId(String(selectedVehicle.driver.driverId)),
      };
    }
    updateOrder(payload);
  }, [order, selectedVehicle, updateOrder]);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (!isOpen || order == null) return;
    setStep('review');
    setSelectedVehicle(undefined);
  }, [isOpen, order?._id]);

  if (!order) return null;

  const steps: BookStep[] = ['review', 'select_vehicle', 'booked'];
  const stepLabels: Record<BookStep, string> = {
    review: t('book_order.step_review'),
    select_vehicle: t('book_order.step_vehicle'),
    booked: t('book_order.step_booked'),
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && handleClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden w-[520px] sm:max-w-[520px]">
        <SheetHeader className="sr-only">
          <SheetTitle>{t('book_order.title')}</SheetTitle>
          <SheetDescription>{t('book_order.review_description')}</SheetDescription>
        </SheetHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-5 pt-4 pb-2 border-b">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`flex items-center gap-1.5 text-xs font-medium ${
                  step === s
                    ? 'text-primary'
                    : i < steps.indexOf(step)
                    ? 'text-success-400'
                    : 'text-muted-foreground'
                }`}>
                <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px]">
                  {i + 1}
                </span>
                {stepLabels[s]}
              </div>
              {i < steps.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5">
          {step === 'review' && <ReviewStep order={order} />}
          {step === 'select_vehicle' && (
            <VehiclePickerList
              orderId={order._id}
              selectedVehicle={selectedVehicle}
              onVehicleSelected={setSelectedVehicle}
            />
          )}
          {step === 'booked' && <BookedStep order={order} />}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t shrink-0">
          {step === 'review' && (
            <Button
              variant="outline"
              className="w-full gap-2 border-primary text-primary hover:bg-primary/5"
              onClick={() => setStep('select_vehicle')}>
              {t('book_order.continue')}
              <ArrowRightIcon className="w-4 h-4 rtl:rotate-180" />
            </Button>
          )}
          {step === 'select_vehicle' && (
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep('review')}>
                {t('c-main:crud_buttons.back')}
              </Button>
              <Button
                className="flex-1"
                onClick={handleBook}
                disabled={!selectedVehicle || isBooking}>
                {isBooking ? (
                  <>
                    <Spinner className="w-4 h-4 me-2" />
                    {t('book_order.booking')}
                  </>
                ) : (
                  t('book_order.book')
                )}
              </Button>
            </div>
          )}
          {step === 'booked' && (
            <Button className="w-full" onClick={handleClose}>
              {t('book_order.back_to_loads')}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ReviewStep({ order }: { order: Order }) {
  const viewerLeg = useViewerLeg(order);
  const { priceUi } = useOrderDisplayInfo({
    order,
    displayForUser: 'carrier',
    viewerLeg,
  });

  return (
    <div className="space-y-4">
      <OrderPriceDetails priceUi={priceUi} />
      <ShipmentDetails order={order} />
      <LoadDetails order={order} />
      <OrderRouteDetails order={order} />
    </div>
  );
}

function BookedStep({ order }: { order: Order }) {
  const { t } = useTranslation(['m-order']);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-8">
      <div className="w-16 h-16 rounded-full bg-success-50 flex items-center justify-center">
        <CircleCheckIcon className="w-8 h-8 text-success-400" />
      </div>
      <h3 className="text-lg font-semibold">{t('book_order.booked_title')}</h3>
      <p className="text-sm text-muted-foreground text-center">
        {t('book_order.booked_description')}
      </p>
      <Badge variant="secondary" className="bg-success-50 text-success-400 text-sm px-4 py-1.5">
        #{order.ref?.short || order._id?.toString().slice(-6)}
      </Badge>
    </div>
  );
}
